import { Translatable } from '../../../localization/model/Text'
import { localTranslate, renderNodeOrLocalTranslate } from '../../../localization/model/Translate'
import { AppStyle } from '../../../theme/style/styleUtilities'
import { handlePassedSx } from '../../../theme/model/Sx'
import { GenericFieldHTMLAttributes } from 'formik/dist/types'
import { InputAdornment, TextField, TextFieldProps, Theme } from '@mui/material'
import { FormLabelType } from '../../model/Form'
import { NotOverridableComponentProps } from '../../../../model/Component'
import { AppBox } from '../../../../component/common/analog/molecule/AppBox'
import { useCallback } from 'react'
import { TEXTAREA_MIN_LENGTH } from '../../model/FormSchema'
import { emptyFunction } from '../../../../model/Function'
import { observer } from 'mobx-react-lite'
import { FIELD_BORDER_RADIUS, FIELD_BORDER_WIDTH } from '../../../theme/util/theme.constant'
import { ColorTokens } from 'src/feature/theme/model/Palette'

type OmittedTopLevelProp = 'placeholder' | 'label' | 'error' | 'disabled'

export type AppTextFieldProps =
  Omit<TextFieldProps & NotOverridableComponentProps, OmittedTopLevelProp>
  & Omit<GenericFieldHTMLAttributes, OmittedTopLevelProp>
  & { placeholder?: Translatable, label?: FormLabelType, isError?: unknown, disabled?: any, isTextarea?: boolean }

export const AppTextField = observer((props: AppTextFieldProps) => {
  const {
    sx = [],
    placeholder = '',
    label = '',
    isError,
    onChange = emptyFunction,
    isTextarea = false,
    multiline = isTextarea,
    minRows = isTextarea ? 5 : undefined,
    inputProps: {
      minLength = isTextarea ? TEXTAREA_MIN_LENGTH : undefined,
      ...rest_inputProps
    } = {},
    InputProps: {
      sx: InputPropsSx = [],
      startAdornment = null,
      endAdornment = null,
      ...rest_InputProps
    } = {},
    FormHelperTextProps: {
      sx: FormHelperTextPropsSx = [],
      ...FormHelperTextPropsRest
    } = {},
    InputLabelProps: {
      sx: InputLabelPropsSx = [],
      ...InputLabelPropsRest
    } = {},
    ...rest
  } = props

  const handleChange = useCallback(onChange, [])

  return (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <AppBox
      fz={12}
      mb={'1.5em'}
      label={renderNodeOrLocalTranslate(label)}
      placeholder={localTranslate(placeholder)}
      type={'text'}
      multiline={multiline}
      minRows={minRows}
      onChange={handleChange}
      error={Boolean(isError)}
      sx={[
        styles.root,
        ...handlePassedSx(sx),
      ]}
      variant={'outlined'}
      inputProps={{
        minLength,
        ...rest_inputProps,
      }}
      InputProps={{
        sx: [
          AppStyle.fieldInput,
          styles.field,
          ...handlePassedSx(InputPropsSx),
        ],
        startAdornment: startAdornment && (
          <InputAdornment position="start" sx={styles.startAdornment}>{startAdornment}</InputAdornment>
        ),
        endAdornment: endAdornment && (
          <InputAdornment position="end" sx={styles.endAdornment}>{endAdornment}</InputAdornment>
        ),
        ...rest_InputProps,
      }}
      FormHelperTextProps={{
        sx: [
          styles.error,
          ...handlePassedSx(FormHelperTextPropsSx),
        ],
        ...FormHelperTextPropsRest,
      }}
      InputLabelProps={{
        sx: [
          styles.label,
          ...handlePassedSx(InputLabelPropsSx),
        ],
        shrink: true,
        required: false, // disable asterisk (*)
        ...InputLabelPropsRest,
      }}
      {...rest}
      component={TextField}
    />
  )
})

const styles = {
  root: {
    padding: 0,
    display: 'flex',
    flex: 1,
  },
  field: (theme: Theme) => ({
    fontSize: 12,
    border: `${FIELD_BORDER_WIDTH}px solid ${theme.palette.border.default}`,
    backgroundColor: 'background.default',
    transition: theme.transitions.create([
      'border-color',
    ]),
    borderRadius: `${FIELD_BORDER_RADIUS}px`,
    '& fieldset': {
      border: 'none',
      borderRadius: `${FIELD_BORDER_RADIUS}px`,
    },
    input: {
      padding: 0,
      fontSize: 12,
      lineHeight: 1.5,
    },
    '&.Mui-focused': {
      border: `${FIELD_BORDER_WIDTH}px solid ${theme.palette.border.focused}`,
    },
    '&.Mui-disabled': {
      cursor: 'not-allowed',
      backgroundColor: ColorTokens.neutral100,
    },
  }),
  label: {
    color: ColorTokens.secondary500,
    position: 'static',
    transform: 'none',
    margin: '0 auto 0.5em 0',
    fontWeight: 400,
    fontSize: 12,
    lineHeight: 1.28,
    fontStyle: 'normal',
    width: 'auto',
    '&.Mui-disabled': {
      color: ColorTokens.neutral400,
    },
  },
  error: {
    width: 'auto',
    fontSize: 12,
    margin: 0,
    mt: '0.625em',
    ml: '0.25em',
    '&:first-letter': {
      textTransform: 'capitalize',
    },
  },
  startAdornment: {
    height: 'unset',
    maxHeight: '1em',
    mr: '0.6em',
    margin: 0,
  },
  endAdornment: {
    height: 'unset',
    maxHeight: '1em',
    ml: '0.6em',
    margin: 0,
  },
}
